import React, { useState, useRef } from 'react';
import { useSpring, animated } from 'react-spring';
import '../styles.css';
import { connect } from 'react-redux';
import { SWAP_DURATION, SM_PINK, SM_GOLD, SM_GREEN, SM_BLUE, SM_PURPLE } from '../const';
import { FLIP_DURATION } from '../const';
import { setBlock, setInit } from '../REDUX/actions';
import { setRestart } from '../../HOME/REDUX/actions';
import * as f from '../logic';
import { playSound } from '../../HOME/utils';
import _ from 'lodash';

const getColor = color => {
  switch (color) {
    case 'red':
      return SM_PINK;
    case 'yellow':
      return SM_GOLD;
    case 'green':
      return SM_GREEN;
    case 'blue':
      return SM_BLUE;
    case 'magenta':
      return SM_PURPLE;
    default:
      return 'transparent';
  }
};

const Cell = React.forwardRef((props, foo) => {
  let { cell, width, selectedCell, over, swapDuration } = props;
  const color = getColor(cell.color);
  const [scale, setScale] = useState(1);
  const scales = 1.8;
  const margin = width * 0.025;
  const fullWidth = width + 2 * margin;
  const dest = cell.dest || f.getDest(cell);
  const dx = (dest.col - cell.col) * fullWidth;
  const dy = (dest.row - cell.row) * fullWidth;
  const l = useRef({ beat: 0, axis: `${_.random(1) ? 'X' : 'Y'}` });

  const transSpring = useSpring({
    transform: `translate(${dx}px, ${dy}px)`,
    config: { duration: props.swapDuration || SWAP_DURATION },
    immediate: !props.steps && (swapDuration || SWAP_DURATION) === SWAP_DURATION,
  });

  const isPulse = () => {
    return f.sameCell(cell, selectedCell) || scale < 1 || (over && l.current.beat < 6);
  };

  const pulseSpring = useSpring({
    transform: `scale(${isPulse() ? scales - scale : 1})`,
    onRest: () => {
      if (over) {
        ++l.current.beat;
      } else {
        l.current.beat = 0;
      }

      if (isPulse()) {
        setScale(scales - scale);
      }
    },
    config: { duration: 200 },
  });

  const flipSpring = useSpring({
    transform: `rotate${l.current.axis}(${props.init ? 90 : 0}deg)`,
    opacity: `${props.init ? 0.5 : 1}`,
    config: { duration: FLIP_DURATION },
  });

  const isSelected = () => f.sameCell(cell, selectedCell);

  const handleTouch = () => {
    l.current.touch = true;
    handleClick();
  };

  const handleMouseDown = () => {
    if (!l.current.touch) {
      handleClick();
    }
  };

  const handleClick = e => {
    if (props.blocked) {
      return;
    }

    if (props.over) {
      f.restart(props);
      return;
    }

    playSound(props, 'click');

    if (isSelected()) {
      props.select(null);
    } else if (selectedCell) {
      props.swap(cell);
    } else {
      props.select(cell);
    }
  };

  const renderCell = () => {
    const style = {
      background: color,
      height: '100%',
      fontSize: `${width / 10}px`,
      gridArea: '1/1',
    };

    let polygon;

    switch (cell.shape) {
      case 'square':
        polygon = 'polygon(30% 30%, 40% 40%, 40% 60%, 60% 60%, 70% 70%, 30% 70%, 30% 30%, 70% 30%, 70% 70%, 60% 60%, 60% 40%, 40% 40%)';
        break;
      case 'rhombus':
        polygon = 'polygon(50% 21%, 50% 35%, 65% 50%, 50% 65%, 50% 79%, 79% 50%, 50% 21%, 21% 50%, 50% 79%, 50% 65%, 35% 50%, 50% 35%)';
        break;
      case 'star':
        polygon =
          'polygon(23.3% 44%, 41.5% 50%, 46.7% 53.7%, 44.7% 59.7%, 50% 56.2%, 55.3% 59.7%, 53.3% 53.7%, 58.5% 50%, 52.1% 50%, 50% 43.8%, 47.9% 50%, 41.5% 50%, 23.3% 44%, 43.7% 44%, 50% 24.3%, 56.3% 44%, 76.7% 44%, 60% 56%, 66.6% 75.5%, 50% 63.4%, 33.4% 75.5%, 40% 56%)';
        break;
      case 'plus':
        polygon = 'polygon(45% 30%, 55% 30%, 55% 45%, 70% 45%, 70% 55%, 55% 55%, 55% 70%, 45% 70%, 45% 55%, 30% 55%, 30% 45%, 45% 45%)';
        break;
      default:
    }

    switch (cell.shape) {
      case 'square':
      case 'rhombus':
      case 'star':
      case 'plus':
        return <div style={{ ...style, WebkitClipPath: polygon, clipPath: polygon }} />;
      case 'circle':
        return <div className="sm-circle" style={{ fontSize: `${width / 10}px`, border: `1em solid ${color}` }} />;
      default:
    }
  };

  let { row, col } = cell;
  const subcolor = color.replace(', 1)', ', 0.35)');

  let style = {
    height: width,
    margin: `${margin}px`,
    width: `${width}px`,
    transform: pulseSpring.transform,
    background: `${cell.valid ? 'transparent' : subcolor}`,
  };

  return (
    <animated.div
      style={{ gridArea: `${row}/${col}`, zIndex: `${cell.swapping ? 1 : 0}`, transform: transSpring.transform }}
      onTouchStart={() => handleTouch()}
      onMouseDown={() => handleMouseDown()}>
      <animated.div style={{ transform: flipSpring.transform, opacity: flipSpring.opacity }}>
        <animated.div className="sm-cell" style={style}>
          <div
            style={{
              gridArea: '1/1',
              background: color,
              width: `${width}px`,
              height: `${width}px`,
              WebkitClipPath: 'polygon(0 0, 10% 10%, 10% 90%, 90% 90%, 90% 10%, 10% 10%, 0 0, 100% 0, 100% 100%, 0 100%)',
              clipPath: 'polygon(0 0, 10% 10%, 10% 90%, 90% 90%, 90% 10%, 10% 10%, 0 0, 100% 0, 100% 100%, 0 100%)',
            }}
          />
          {renderCell()}
        </animated.div>
      </animated.div>
    </animated.div>
  );
});

const mapStateToProps = ({ sm, common }) => {
  return { ...sm, ...common };
};

export default connect(
  mapStateToProps,
  { setBlock, setInit, setRestart },
  null,
  { forwardRef: true }
)(Cell);
